// Leave-Shift Conflict Resolution Modal
// Phase 5 of LEAVE_SHIFT_CONFLICT_RESOLUTION_IMPLEMENTATION.md

import { useState, useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, AlertTriangle, Calendar, Clock, UserPlus, Trash2, ArrowRight } from 'lucide-react';
import { useEligibleReplacements, useApproveLeaveWithResolution } from '@/hooks/leave/useLeaveConflicts';
import type { ShiftConflict, ConflictResolutionAction, ShiftResolution } from '@/services/leave/conflictTypes';
import { generateInitials } from '@/utils/avatarUtils';
import { getLeaveTypeLabel } from '@/constants/leaveTypes';

interface LeaveRequestInfo {
  id: string;
  profile_id: string;
  company_id: string;
  start_date: string;
  end_date: string;
  leave_type: string;
  profile: {
    first_name: string | null;
    last_name: string | null;
    email: string;
    avatar_url: string | null;
  };
}

interface LeaveConflictResolutionModalProps {
  open: boolean;
  onClose: () => void;
  conflicts: ShiftConflict[];
  leaveRequest: LeaveRequestInfo;
  onSuccess?: () => void;
}

interface ShiftResolutionState {
  action: ConflictResolutionAction;
  replacementProfileId?: string;
}

export function LeaveConflictResolutionModal({
  open,
  onClose,
  conflicts,
  leaveRequest,
  onSuccess,
}: LeaveConflictResolutionModalProps) {
  // Per-shift resolution state
  const [resolutions, setResolutions] = useState<Record<string, ShiftResolutionState>>({});
  
  // Get all shift IDs for eligible replacements query
  const shiftIds = useMemo(() => conflicts.map(c => c.shift_id), [conflicts]);
  
  // Fetch eligible replacements
  const { data: replacements, isLoading: isLoadingReplacements } = useEligibleReplacements(
    shiftIds,
    leaveRequest.company_id,
    open && conflicts.length > 0
  );
  
  // Mutation for approving with resolution
  const approveWithResolution = useApproveLeaveWithResolution();
  
  // Initialize state when conflicts change
  useEffect(() => {
    if (conflicts.length > 0) {
      const initial: Record<string, ShiftResolutionState> = {};
      conflicts.forEach(c => {
        initial[c.shift_id] = { action: 'continue' };
      });
      setResolutions(initial);
    }
  }, [conflicts]);
  
  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setResolutions({});
    }
  }, [open]);
  
  // Update resolution for a specific shift
  const updateResolution = (shiftId: string, updates: Partial<ShiftResolutionState>) => {
    setResolutions(prev => ({
      ...prev,
      [shiftId]: {
        ...prev[shiftId],
        ...updates,
        // Clear replacement when action changes away from 'assign'
        ...(updates.action && updates.action !== 'assign' ? { replacementProfileId: undefined } : {}),
      },
    }));
  };
  
  // Check if form is valid for submission
  const isValidForSubmit = useMemo(() => {
    return conflicts.every(conflict => {
      const resolution = resolutions[conflict.shift_id];
      if (!resolution) return false;
      
      // If action is 'assign', a replacement must be selected
      if (resolution.action === 'assign' && !resolution.replacementProfileId) {
        return false;
      }
      
      return true;
    });
  }, [conflicts, resolutions]);
  
  // Handle submit
  const handleSubmit = async () => {
    const payload: ShiftResolution[] = conflicts.map(conflict => ({
      shift_id: conflict.shift_id,
      action: resolutions[conflict.shift_id]?.action || 'continue',
      replacement_profile_id: resolutions[conflict.shift_id]?.replacementProfileId,
    }));
    
    try {
      await approveWithResolution.mutateAsync({
        leaveRequestId: leaveRequest.id,
        resolutions: payload,
      });
      onSuccess?.();
      onClose();
    } catch (error) {
      // Error is handled by the mutation's onError
      // Close modal as per F2 mitigation (race condition recovery)
      onClose();
    }
  };
  
  // Format shift time
  const formatShiftTime = (conflict: ShiftConflict) => {
    try {
      const start = new Date(conflict.shift_start_time);
      const end = new Date(conflict.shift_end_time);
      return `${format(start, 'HH:mm')} - ${format(end, 'HH:mm')}`;
    } catch {
      return 'Time unavailable';
    }
  };
  
  // Format shift date
  const formatShiftDate = (conflict: ShiftConflict) => {
    try {
      const date = new Date(conflict.shift_date);
      return format(date, 'EEE, MMM d, yyyy');
    } catch {
      return conflict.shift_date;
    }
  };
  
  // Get available replacements (filter to show available first)
  const sortedReplacements = useMemo(() => {
    if (!replacements) return [];
    return [...replacements].sort((a, b) => {
      // Available employees first
      if (a.is_available && !b.is_available) return -1;
      if (!a.is_available && b.is_available) return 1;
      // Then alphabetically
      return a.profile_name.localeCompare(b.profile_name);
    });
  }, [replacements]);
  
  const employeeName = [
    leaveRequest.profile.first_name,
    leaveRequest.profile.last_name
  ].filter(Boolean).join(' ') || leaveRequest.profile.email;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Resolve Schedule Conflicts
          </DialogTitle>
          <DialogDescription>
            This leave request overlaps with existing shifts. Choose how to handle each conflict before approving.
          </DialogDescription>
        </DialogHeader>
        
        {/* Employee & Leave Info */}
        <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
          <Avatar className="h-12 w-12">
            <AvatarImage src={leaveRequest.profile.avatar_url || undefined} />
            <AvatarFallback>
              {generateInitials(leaveRequest.profile.first_name, leaveRequest.profile.last_name)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate">{employeeName}</p>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Badge variant="secondary">{getLeaveTypeLabel(leaveRequest.leave_type)}</Badge>
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {format(new Date(leaveRequest.start_date), 'MMM d')} - {format(new Date(leaveRequest.end_date), 'MMM d, yyyy')}
              </span>
            </div>
          </div>
        </div>
        
        {/* Conflicts List */}
        <ScrollArea className="flex-1 min-h-0 pr-4">
          <div className="space-y-4">
            {conflicts.map((conflict, index) => {
              const resolution = resolutions[conflict.shift_id] || { action: 'continue' };
              
              return (
                <Card key={conflict.shift_id} className="border-amber-200/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          Shift {index + 1}
                        </Badge>
                        {formatShiftDate(conflict)}
                      </span>
                      <span className="flex items-center gap-1 text-muted-foreground font-normal">
                        <Clock className="h-3 w-3" />
                        {formatShiftTime(conflict)}
                      </span>
                    </CardTitle>
                    {(conflict.shift_code || conflict.shift_note) && (
                      <p className="text-xs text-muted-foreground">
                        {conflict.shift_code && <span className="font-medium">{conflict.shift_code}</span>}
                        {conflict.shift_code && conflict.shift_note && ' - '}
                        {conflict.shift_note}
                      </p>
                    )}
                  </CardHeader>
                  <CardContent className="pt-2">
                    <RadioGroup
                      value={resolution.action}
                      onValueChange={(value) => updateResolution(conflict.shift_id, { action: value as ConflictResolutionAction })}
                      className="space-y-3"
                    >
                      {/* Assign Replacement */}
                      <div className="flex items-start gap-3">
                        <RadioGroupItem value="assign" id={`assign-${conflict.shift_id}`} className="mt-1" />
                        <div className="flex-1 space-y-2">
                          <Label 
                            htmlFor={`assign-${conflict.shift_id}`} 
                            className="flex items-center gap-2 cursor-pointer"
                          >
                            <UserPlus className="h-4 w-4 text-primary" />
                            Assign replacement
                          </Label>
                          {resolution.action === 'assign' && (
                            <Select
                              value={resolution.replacementProfileId || ''}
                              onValueChange={(value) => updateResolution(conflict.shift_id, { replacementProfileId: value })}
                              disabled={isLoadingReplacements}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder={isLoadingReplacements ? "Loading..." : "Select employee"} />
                              </SelectTrigger>
                              <SelectContent>
                                {sortedReplacements.length === 0 ? (
                                  <div className="p-2 text-sm text-muted-foreground text-center">
                                    No employees available
                                  </div>
                                ) : (
                                  sortedReplacements.map((emp) => (
                                    <SelectItem 
                                      key={emp.profile_id} 
                                      value={emp.profile_id}
                                      disabled={!emp.is_available}
                                    >
                                      <div className="flex items-center justify-between w-full">
                                        <span>{emp.profile_name}</span>
                                        {!emp.is_available && (
                                          <Badge variant="secondary" className="ml-2 text-xs">
                                            {emp.unavailable_reason}
                                          </Badge>
                                        )}
                                      </div>
                                    </SelectItem>
                                  ))
                                )}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      </div>
                      
                      {/* Remove Shift */}
                      <div className="flex items-center gap-3">
                        <RadioGroupItem value="remove" id={`remove-${conflict.shift_id}`} />
                        <Label 
                          htmlFor={`remove-${conflict.shift_id}`} 
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                          Remove shift (permanently delete)
                        </Label>
                      </div>
                      
                      {/* Continue Anyway */}
                      <div className="flex items-center gap-3">
                        <RadioGroupItem value="continue" id={`continue-${conflict.shift_id}`} />
                        <Label 
                          htmlFor={`continue-${conflict.shift_id}`} 
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <ArrowRight className="h-4 w-4 text-muted-foreground" />
                          Continue anyway (leave shift assigned)
                        </Label>
                      </div>
                    </RadioGroup>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </ScrollArea>
        
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} disabled={approveWithResolution.isPending}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={!isValidForSubmit || approveWithResolution.isPending}
          >
            {approveWithResolution.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Approving...
              </>
            ) : (
              'Approve with Resolutions'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
